/**
 * 战机的画布绘制（Canvas 2D）：竖屏场地 360×640（y 向下，规则核心的逻辑单位）。只画，不改状态。
 * 颜色全从主题令牌读（readPalette，与贪吃、篮球同一套），深色模式跟着变；分数、心、波次、绝招、驾驶舱里的 TA 都在 DOM 里。
 * - 飞机能飞的那一条（y 380～610）底色稍深，手指往哪拖心里有数；
 * - 敌机按种类画不同形状（无人机三角、之字机菱形、石块圆、炮台方块带炮管），多血的标出还剩几格；
 * - 飞机的座舱涂 TA 的颜色：TA 坐在里面；被撞后的无敌时间闪烁（减少动态效果时固定半透明）。
 */
import { ENEMY, PLANE_MAX_Y, PLANE_MIN_Y, type Enemy, type FlightState, type Shot } from "../core";
import { drawFloat, petAlpha, type Palette } from "./draw";
import type { FloatText } from "./feedback";
import type { Fit } from "./viewport";

/** 场地的逻辑尺寸（与规则核心一致） */
export const FLIGHT_ARENA = { width: 360, height: 640 } as const;

export interface FlightSceneInput {
  state: FlightState;
  fit: Fit;
  dpr: number;
  palette: Palette;
  floats: readonly FloatText[];
  /** 手指拖向的目标点（画个小圈）；null 不画 */
  target: { x: number; y: number } | null;
  nowMs: number;
  reducedMotion: boolean;
}

/** 敌机的颜色：按种类分开，石块是障碍用墙色 */
export function enemyColor(kind: Enemy["kind"], p: Palette): string {
  return kind === "drone" ? p.bad : kind === "zigzag" ? p.treat : kind === "rock" ? p.wall : p.crate;
}

function drawEnemy(ctx: CanvasRenderingContext2D, enemy: Enemy, p: Palette) {
  const { x, y, radius: r } = enemy;
  ctx.fillStyle = enemyColor(enemy.kind, p);
  ctx.beginPath();
  if (enemy.kind === "drone") {
    // 三角，尖朝下（往下冲）
    ctx.moveTo(x - r, y - r * 0.7);
    ctx.lineTo(x + r, y - r * 0.7);
    ctx.lineTo(x, y + r);
    ctx.closePath();
  } else if (enemy.kind === "zigzag") {
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r, y);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - r, y);
    ctx.closePath();
  } else if (enemy.kind === "rock") {
    ctx.arc(x, y, r, 0, Math.PI * 2);
  } else {
    ctx.roundRect(x - r, y - r, r * 2, r * 2, 4);
  }
  ctx.fill();
  if (enemy.kind === "turret") {
    // 炮管朝下
    ctx.fillStyle = p.ink;
    ctx.fillRect(x - 3, y + r - 2, 6, 10);
  }
  // 多血的：头顶一排小点，还剩几格画几个
  const full = ENEMY[enemy.kind].hp;
  if (full > 1) {
    ctx.fillStyle = p.ink;
    for (let i = 0; i < enemy.hp; i++) {
      ctx.beginPath();
      ctx.arc(x - ((full - 1) * 6) / 2 + i * 6, y - r - 6, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawShot(ctx: CanvasRenderingContext2D, shot: Shot, color: string, outline: string | null) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(shot.x, shot.y, shot.radius, 0, Math.PI * 2);
  ctx.fill();
  if (outline) {
    ctx.strokeStyle = outline;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

function drawPlane(ctx: CanvasRenderingContext2D, input: FlightSceneInput) {
  const { state, palette: p } = input;
  const { x, y, radius: r } = state.plane;
  ctx.save();
  ctx.globalAlpha = petAlpha(state.plane.invulnerableMs, input.nowMs, input.reducedMotion);
  // 机身与两翼
  ctx.fillStyle = p.gate;
  ctx.beginPath();
  ctx.moveTo(x, y - r * 1.4);
  ctx.lineTo(x + r * 1.3, y + r * 0.8);
  ctx.lineTo(x, y + r * 0.4);
  ctx.lineTo(x - r * 1.3, y + r * 0.8);
  ctx.closePath();
  ctx.fill();
  // 座舱：TA 的颜色
  ctx.fillStyle = p.pet;
  ctx.beginPath();
  ctx.arc(x, y - r * 0.15, r * 0.45, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export function drawFlightScene(ctx: CanvasRenderingContext2D, input: FlightSceneInput): void {
  const { state, fit, dpr, palette: p } = input;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.setTransform(dpr * fit.scale, 0, 0, dpr * fit.scale, dpr * fit.offsetX, dpr * fit.offsetY);
  ctx.fillStyle = p.floor;
  ctx.fillRect(0, 0, FLIGHT_ARENA.width, FLIGHT_ARENA.height);
  // 飞机能飞的那一条
  ctx.fillStyle = p.grid;
  ctx.fillRect(0, PLANE_MIN_Y - 16, FLIGHT_ARENA.width, PLANE_MAX_Y - PLANE_MIN_Y + 32);
  for (const enemy of state.enemies) drawEnemy(ctx, enemy, p);
  for (const pellet of state.pellets) drawShot(ctx, pellet, p.treat, p.ink);
  for (const bullet of state.bullets) drawShot(ctx, bullet, p.good, null);
  if (input.target) {
    ctx.strokeStyle = p.info;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(input.target.x, input.target.y, 7, 0, Math.PI * 2);
    ctx.stroke();
  }
  drawPlane(ctx, input);
  for (const f of input.floats) drawFloat(ctx, f, input);
}
